import { Component, NgZone, OnInit } from '@angular/core';
import { NuveiService, SavedNuveiApplicationForm } from "../../../services/nuvei.service";
import { ActivatedRoute, Router } from "@angular/router";
import { map, take } from "rxjs/operators";
import { BusinessService } from "../../../services/business.service";
import { FormControl } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { SignatureComponent } from "../../../modals/signature/signature.component";
import { ModalBehavior, ModalsService } from "../../../../../../common/src/lib/services/modals.service";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { timer } from "rxjs";
import moment from "moment";

declare global {
  interface Number {
    inRange(from: number, to: number): boolean;
  }
}

Number.prototype.inRange = function (from: number, to: number): boolean {
  return (this as number) >= from &&  (this as number) < to;
};

const LOCAL_STORAGE_TOKEN_KEY_PREFIX = 'SETOKEN_'

@Component({
  selector: 'app-nuvei-application-guarantor',
  templateUrl: './nuvei-application-guarantor.component.html',
  styleUrls: ['./nuvei-application-guarantor.component.scss']
})
export class NuveiApplicationGuarantorComponent implements OnInit {

  errorMessage?: string;
  form!: SavedNuveiApplicationForm;
  businessId!: string;

  signatureApproval?: string;
  waitingForSignatureApproval = false;
  submitting = false;

  submitCheckboxControl = new FormControl<boolean>(false);

  documentUrl?: SafeUrl;

  extendDocument = false;

  downloadAvailable = true;
  downloadTimer$ = timer(0, 1000).pipe(
      map(_ => {
        if(!this.form.formDocument!.documentExpiration)
          return null;
        if(moment(this.form.formDocument!.documentExpiration).isBefore()) {
          this.zone.run(() => {
            this.downloadAvailable = false
          });
        }
        return moment(moment(this.form.formDocument!.documentExpiration).diff(moment())).format('mm:ss');
      })
  );

  get userName() {
    if(!this.form.businessOwnersForm)
      return null;
    const guarantor = this.form.businessOwnersForm.owners![this.form.businessOwnersForm.guarantor!];
    return `${guarantor.firstName} ${guarantor.lastName}`;
  }

  get isFinalDocument() {
    return this.form.signatures.guarantor?.submitted;
  }

  get localToken() {
    return localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY_PREFIX + this.businessId);
  }

  constructor(
      private domSanitizer: DomSanitizer,
      private businessService: BusinessService,
      private nuveiService: NuveiService,
      private modalsService: ModalsService,
      private route: ActivatedRoute,
      private zone: NgZone,
      private router: Router,
  ) {}

  async ngOnInit() {
    const params = this.route.snapshot.params;
    const queryParams = this.route.snapshot.queryParams;
    this.businessId = params['businessId'];
    let token = queryParams['t'];
    if(!token) {
      token = this.localToken;
    } else {
      localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY_PREFIX + this.businessId, token);
    }

    if(!this.businessId || !token) {
      this.close();
      return;
    }

    try {
      const form = await this.nuveiService.getApplicationForm(this.businessId, token);
      if(!form?.signatures?.owner?.submitted || form.businessOwnersForm!.guarantor === 0) {
        this.errorMessage = 'We couldn\'t find a document for signature';
        return;
      }
      this.form = form;

      let link = form.formDocument!.documentUrl;
      if(!this.isFinalDocument)
        link += '#toolbar=0';
      else if(form.signatures.guarantor?.approval) {
        this.signatureApproval = form.signatures.guarantor?.approval;
        this.submitCheckboxControl.setValue(true, { emitEvent: true });
      }

      this.documentUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(link);

      this.nuveiService.addEvent(this.businessId, 'View', this.localToken!);
    } catch (e: any) {
      this.errorMessage = 'We couldn\'t find a document for signature';
    }
  }

  async requestApproval(event: MatCheckboxChange) {
    const isRequest = event.checked;
    try {
      if(isRequest) {
        this.waitingForSignatureApproval = true;
        const approval = await this.nuveiService.requestApproval(this.businessId, 'signature', this.localToken!);
        this.zone.run(() => this.signatureApproval = approval);
        this.waitingForSignatureApproval = false;
      } else if (this.signatureApproval) {
        delete this.form.signatures.guarantor;
        const result = await this.nuveiService.revokeApproval(this.businessId, this.signatureApproval!, 'signature', this.localToken!);
        this.zone.run(() => {
          if(result)
            this.documentUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(result + '#toolbar=0');
        });
        this.signatureApproval = undefined;
      }
    } catch (e: any) {
      if(isRequest || (e.context.status as number).inRange(400, 500))
        this.errorMessage = 'We couldn\'t find a document for signature';
      this.submitCheckboxControl.setValue(false);
      this.signatureApproval = undefined;
      this.waitingForSignatureApproval = false;
    }
  }

  async openSignatureModal() {
    const guarantor = this.form.businessOwnersForm!.owners![this.form.businessOwnersForm!.guarantor!];
    this.nuveiService.addEvent(this.businessId, 'Click To Sign', this.localToken!);

    this.modalsService.open(SignatureComponent, {
      behavior: ModalBehavior.Dialog,
      data: {
        saveSignature: (async (data: string) => {
          const file = this.dataURItoBlob(data);
          try {
            await this.nuveiService.uploadSignature(this.businessId, file, this.signatureApproval!, this.localToken!);

            this.zone.run(() => {
              this.form.signatures.guarantor = {
                downloadUrl: data,
                signerName: `${guarantor.firstName} ${guarantor.lastName}`,
                timestamp: new Date(),
                submitted: false,
                approval: this.signatureApproval!
              };
            });
          } catch (e: any) {
            if((e.context.status as number).inRange(400, 500))
              this.errorMessage = 'We couldn\'t find a document for signature';
          }

        }).bind(this)
      },
    });
  }

  async submit() {
    this.submitting = true;
    try {
      const documentUrl = await this.nuveiService.finalizeSignature(this.businessId, this.localToken!);
      this.documentUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(documentUrl);
      this.form.signatures.guarantor!.submitted = true;
    } catch (e: any) {
      if((e.context.status as number).inRange(400, 500))
        this.errorMessage = 'We couldn\'t find a document for signature';
    }
    this.submitting = false;
  }

  dataURItoBlob(dataURI: string): Blob {
    const byteString = atob(dataURI.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: 'image/png' });
  }

  async close() {
    const businesses = await this.businessService.availableBusinesses$.pipe(take(1)).toPromise();
    if(businesses.length > 0) {
      this.router.navigate(['/']);
    } else {
      this.router.navigateByUrl('https://slickestimates.com');
    }
  }

}
